import { FormActivitySphereMultiselect } from '@entities/activitySphere';
import { useUpdateCandidateResume } from '@entities/candidates';
import { FormGuideEmploymentType } from '@entities/employmentType';
import { FormGuideScheduleWork } from '@entities/scheduleWork';
import { FormGuideSkill } from '@entities/skill';
import { Button, Flex, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { Form, FormRichTextEditor, FormTextarea } from '@shared/ui';
import { showNotification } from '@shared/utils';
import { $CandidateResumeEditSchema } from '../schema';
import type { CandidateResumeEditValues } from '../types';
import { getCandidateResumeUpdateRequest } from '../utils/getCandidateResumeUpdateRequest';
import { CandidateResumeSalaryField } from './CandidateResumeSalaryField';

type CandidateResumeFormProps = {
  candidateId: number;
  resumeId: number;
  initialValues: CandidateResumeEditValues;
  onCancel: () => void;
  onSuccess: () => void;
};

export const CandidateResumeForm = ({
  candidateId,
  resumeId,
  initialValues,
  onCancel,
  onSuccess,
}: CandidateResumeFormProps) => {
  const { mutate: updateCandidateResume, isPending } =
    useUpdateCandidateResume();
  const form = useForm<CandidateResumeEditValues>({
    initialValues,
    validate: zodResolver($CandidateResumeEditSchema),
  });

  const handleSubmit = (values: CandidateResumeEditValues) => {
    updateCandidateResume(
      {
        candidateId,
        resumeId,
        request: getCandidateResumeUpdateRequest(values),
      },
      {
        onSuccess,
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось обновить условия работы',
          });
        },
      }
    );
  };

  return (
    <Form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex gap={24} direction='column'>
        <Title order={4}>Условия работы</Title>

        <Flex direction='column' gap={16}>
          <FormActivitySphereMultiselect
            title='Профессия'
            description='Выберите профессиональную сферу кандидата'
            disabled={isPending}
            key={form.key('activitySphereIds')}
            {...form.getInputProps('activitySphereIds')}
          />
          <FormTextarea
            title='Желаемая должность'
            description='Укажите желаемую должность кандидата'
            placeholder='Введите должность'
            disabled={isPending}
            key={form.key('desiredJob')}
            {...form.getInputProps('desiredJob')}
          />
          <FormGuideSkill
            disabled={isPending}
            key={form.key('skillIds')}
            {...form.getInputProps('skillIds')}
          />
          <FormRichTextEditor
            title='Опыт работы'
            description='Опишите опыт работы кандидата'
            placeholder='Введите опыт работы'
            editable={!isPending}
            key={form.key('experience')}
            {...form.getInputProps('experience')}
          />
          <CandidateResumeSalaryField
            salary={form.values.salary}
            currencyId={form.values.currencyId}
            paymentPeriodId={form.values.paymentPeriodId}
            disabled={isPending}
            onSalaryChange={value => form.setFieldValue('salary', value)}
            onCurrencyChange={value => form.setFieldValue('currencyId', value)}
            onPaymentPeriodChange={value =>
              form.setFieldValue('paymentPeriodId', value)
            }
          />
          <FormGuideScheduleWork
            disabled={isPending}
            key={form.key('scheduleWorkIds')}
            {...form.getInputProps('scheduleWorkIds')}
          />
          <FormGuideEmploymentType
            disabled={isPending}
            key={form.key('employmentTypeIds')}
            {...form.getInputProps('employmentTypeIds')}
          />
        </Flex>

        <Flex gap={8} mt={16}>
          <Button type='button' variant='gray' onClick={onCancel}>
            Назад
          </Button>
          <Button type='submit' loading={isPending} disabled={isPending}>
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};
